import { v4 as uuidv4 } from 'uuid';
import { type StatsigUser } from '@statsig/react-bindings';

import { getCookie, setCookie } from 'modules/common/utils';
import { collectiveTracking } from 'modules/common/components/AnalyticsTracking/utils';

/**
 * Identity marker shared with collective-landing so Statsig rules can target a
 * single value to exclude bot/e2e traffic from experiments and gates (CA-5523).
 */
const BOT_ID = 'bot';

// Same detection regex as collective-landing's StatsigServerProvider — the e2e
// runner's `CollectiveE2EBot/1.0` suffix matches `bot(?!tom)`.
const BOT_USER_AGENT_PATTERN =
    /elb-healthchecker|amazoncloudwatch|aws-cli|aws-sdk|monitor|crawler|spider|bot(?!tom)|check_http|health.*check|pingdom|datadog|newrelic|uptimerobot|nagios|zabbix|synthetic|curl|wget|python-requests|headlesschrome|puppeteer|axios|go-http-client|java\/\d|apache-httpclient|okhttp|jmeter|gatling|node-fetch/i;

let cachedIsBotUser: boolean | undefined;

/**
 * Whether this session belongs to an automated client (e2e runner, headless
 * browser, monitor). The user agent cannot change mid-session, so the result
 * is cached for the page lifetime.
 */
export function isBotUser(): boolean {
    if (cachedIsBotUser === undefined) {
        cachedIsBotUser = BOT_USER_AGENT_PATTERN.test(window.navigator.userAgent ?? '');
    }

    return cachedIsBotUser;
}

/**
 * Cached for the page lifetime so the anonymous id is stable even when the
 * cookie cannot be persisted (blocked cookies, misconfigured domain). Without
 * this, a failed cookie write would mint a fresh uuid on every call — and since
 * this runs inside a reactive effect on logout, that would loop updateUserAsync.
 */
let cachedStatsigUserId: string | undefined;

/**
 * Returns the persistent anonymous Statsig identity, generating and storing a
 * long-lived cookie id on first use. This id is the stableID surrogate: reusing
 * it keeps experiment bucketing consistent across sessions and logout/relogin.
 */
function getOrCreateStatsigUserId(): string {
    if (cachedStatsigUserId) {
        return cachedStatsigUserId;
    }

    let statsig_user_id = getCookie('statsig_user_id');

    if (!statsig_user_id) {
        const expireDate = new Date();
        statsig_user_id = uuidv4();
        expireDate.setTime(expireDate.getTime() + 365 * 24 * 60 * 60 * 1000);
        setCookie(
            'statsig_user_id',
            statsig_user_id,
            expireDate,
            import.meta.env.VITE_TRACKING_SCRIPT_COOKIE_DOMAIN // FIXME - change to something like VITE_COOKIE_DOMAIN for general use
        );
    }

    cachedStatsigUserId = statsig_user_id;

    return cachedStatsigUserId;
}

/**
 * The logged-out Statsig user. Matches what a cold, unauthenticated boot
 * produces, so resetting to it on logout does not churn the anonymous identity.
 */
export function buildAnonymousStatsigUser(): StatsigUser {
    if (isBotUser()) {
        // Same identity landing assigns to bots — no cookie id is minted so the
        // automated session never gets a persistent human-looking identity.
        return {
            userID: BOT_ID,
            customIDs: {
                segmentAnonymousID: BOT_ID,
                stableID: BOT_ID,
            },
        };
    }

    const segmentAnonymousID =
        typeof collectiveTracking?.getAnonymousId === 'function' ? collectiveTracking.getAnonymousId() : undefined;

    return {
        userID: getOrCreateStatsigUserId(),
        customIDs: segmentAnonymousID ? { segmentAnonymousID } : {},
    };
}
